import {defineStore} from 'pinia';
import * as api from "../services/api";
import {usePage} from "@inertiajs/inertia-vue3";
import {useCampaignStore} from "./CampaignStore";
import {parseBool} from "../utils/helpers";
import {t} from "@/i18n";
import {
    buildSavePayload,
    createOriginal,
    dirtyFields,
    dirtySummary,
    isDirty as draftIsDirty,
    restoreOriginal,
} from "../utils/editorDraft.mjs";

export const useStepEditStore = defineStore('StepEditStore', {
    state: () => {
        return {
            processing: false,
            uuid: null,
            /**
             * Ausgangsmarke des geladenen Schritts.
             *
             * Kommt aus steps.edit und geht beim Speichern wieder mit. Hat
             * inzwischen jemand anderes gespeichert, weist der Server ab,
             * statt dessen Fassung zu überschreiben.
             *
             * @see \App\Http\Controllers\StepController::update()
             */
            revision: null,
            label: null,
            lib: [],
            include: [],
            custom_css: null,
            script: [],
            content: {},
            info: {},
            editor_config: {},
            info_editor: {},
            /**
             * Eigene Übersetzungen der App für ihren Editor,
             * {en_gb: {"Titel": "Heading"}}. Gehen dem Wörterbuch vor.
             *
             * @see resources/js/i18n/appEditorTexts.mjs
             */
            editor_translations: {},
            window_items: [],
            window_info: [],
            current_window: 0,
            current_window_props: null,
            window_items_opened: [],
            window_props_history: [],
            deactivated: 0,
            /**
             * Diagnosedaten zur aktiven Editor-Version.
             * Nur der Expertenmodus zeigt sie an.
             */
            editor_meta: null,
            /**
             * Kann dieser Schritt die echte App als Vorschau zeigen?
             * {available: bool, reason: string|null} - kommt vom Server.
             *
             * @see \App\Http\Controllers\StepController::appPreviewState()
             */
            app_preview: null,
            /**
             * Nachbarschritte desselben Ablauftags.
             * {prev: {uuid, label}|null, next: {uuid, label}|null}
             *
             * @see \App\Http\Controllers\StepController::neighboursOf()
             */
            neighbours: null,
            /**
             * Unveränderter Ausgangszustand beim Öffnen des Editors.
             *
             * content/info/label/custom_css oben sind die lokale ARBEITSKOPIE.
             * Feldkomponenten schreiben ausschließlich dorthin - der globale
             * StepsStore und der Server bleiben bis zum expliziten Speichern
             * unberührt.
             *
             * @see resources/js/utils/editorDraft.mjs
             */
            original: null,
            /** Läuft gerade ein Save? Sperrt die Aktion gegen Doppelklick. */
            saving: false,
            /** Letzter Save-Fehler. Der Draft bleibt dabei erhalten. */
            save_error: null,
        };
    },
    getters: {
        /**
         * Die aktuelle lokale Arbeitskopie.
         */
        draft: (state) => ({
            label: state.label,
            content: state.content,
            info: state.info,
            custom_css: state.custom_css,
        }),
        /**
         * Gibt es ungespeicherte Änderungen?
         *
         * Grundlage für die Dirty-Anzeige und den Verwerfen-Dialog.
         */
        isDirty() {
            return draftIsDirty(this.original, this.draft);
        },
        dirtyFields() {
            return dirtyFields(this.original, this.draft);
        },
        /**
         * Welche Bereiche wurden geändert? Für die Anzeige im Dialog.
         *
         * editorDraft liefert Sprachschlüssel; übersetzt wird hier, im
         * Getter, damit die Liste beim Sprachwechsel mitzieht.
         */
        dirtySummary() {
            return dirtySummary(this.original, this.draft).map(key => t(key));
        },
        contentEditableForCurrentUser: (state) => {
            // Empty editor_config
            if ( ! state.editor_config || Array.isArray(state.editor_config) && state.editor_config.length === 0) return false;
            const user = usePage().props.value.auth.user;

            // Check for "operator_only" flag
            if (Object.hasOwn(state.editor_config, 'operator_only')) {
                if (parseBool(state.editor_config.operator_only)) {
                    return user.operator;
                }
            }

            return true;
        },
        infoEditableForCurrentUser: (state) => {
            // Empty info_editor
            if ( ! state.info_editor || Array.isArray(state.info_editor) && state.info_editor.length === 0) return false;
            const user = usePage().props.value.auth.user;

            // Check for "info_editor" flag
            if (Object.hasOwn(state.info_editor, 'operator_only')) {
                if (parseBool(state.info_editor.operator_only)) {
                    return user.operator;
                }
            }

            return true;
        }
    },
    actions: {
        reset() {
            this.processing = false;
            this.uuid = null;
            this.label = null;
            this.lib = [];
            this.include = [];
            this.custom_css = null;
            this.script = [];
            this.content = {};
            this.info = {};
            this.editor_config = {};
            this.info_editor = {};
            this.editor_translations = {};
            this.window_items = [];
            this.window_info = [];
            this.current_window = 0;
            this.current_window_props = null;
            this.window_items_opened = [];
            this.window_props_history = [];
            this.deactivated = 0;
            this.editor_meta = null;
            this.app_preview = null;
            this.neighbours = null;
            this.original = null;
            this.saving = false;
            this.save_error = null;
        },
        /**
         * Ausgangszustand einfrieren.
         *
         * Wird direkt nach dem Laden der Step-Daten aufgerufen. Ab hier ist
         * jede Feldänderung eine Änderung an der Arbeitskopie und wird als
         * "ungespeichert" erkannt.
         */
        captureOriginal() {
            this.original = createOriginal({
                label: this.label,
                content: this.content,
                info: this.info,
                custom_css: this.custom_css,
            });
            this.save_error = null;
        },
        /**
         * Änderungen verwerfen: Arbeitskopie auf den Ausgangszustand
         * zurücksetzen. Der gespeicherte Stand auf dem Server bleibt
         * unverändert - es wird kein Request gesendet.
         */
        discardChanges() {
            if ( ! this.original) return;

            const restored = restoreOriginal(this.original);

            this.label = restored.label;
            this.content = restored.content;
            this.info = restored.info;
            this.custom_css = restored.custom_css;
            this.save_error = null;

            // Die Panelstruktur zeigt auf Indizes der Arbeitskopie.
            this.resetWindows();
        },
        parseEditorConfig() {
            try {
                this.window_items = [];
                if (this.editor_config && this.editor_config.items) {
                    this.extractSubPanel(this.editor_config.items, this.window_items);
                } else {
                    this.editor_config = null;
                }
            } catch (e) {
                console.log(e);
                this.window_items = [];
            }
        },
        parseInfoEditor() {
            try {
                this.window_info = [];
                if (this.info_editor && this.info_editor.items) {
                    this.extractSubPanel(this.info_editor.items, this.window_info);
                } else {
                    this.info_editor = null;
                }
            } catch (e) {
                console.log(e);
                this.window_info = [];
            }
        },
        /**
         * extract all sub panels and create a flat array for v-window/v-window-item usage
         *
         * Recursive Function!
         *
         * @param elements
         * @param panels
         * @returns {number}
         */
        extractSubPanel(elements, panels) {
            const items = [];
            let panel_index = panels.push(items) - 1;

            elements.forEach(e => {
                if (e.component === 'SubPanel') {
                    items.push({
                        "component": "SubPanel",
                        "label": e.label,
                        "hint": e.hint,
                        "json_path": e.json_path,
                        "component_config": {
                            ...e.component_config,
                            "window_target_index": this.extractSubPanel(e.items, panels),
                        },
                        "vuetify_config": e.vuetify_config,
                    });
                } else {
                    items.push(e);
                }
            });

            return panel_index;
        },
        /**
         * Reset the sub panel navigation.
         *
         * The window state belongs to *one* editor (content or info). When the
         * user switches between them, we have to start at the top level again.
         */
        resetWindows() {
            this.current_window = 0;
            this.current_window_props = null;
            this.window_items_opened = [];
            this.window_props_history = [];
        },
        openSubPanel(panel_props) {
            this.window_items_opened.unshift(panel_props.window_index);
            this.current_window_props = panel_props;
            this.window_props_history.unshift(panel_props);

            // For Vuetify
            this.current_window = panel_props.window_index;
        },
        closeSubPanel() {
            // Shift current
            this.window_props_history.shift();
            this.window_items_opened.shift();

            // Set to previous settings
            this.current_window_props = this.window_props_history[0] || null;
            this.current_window = this.window_items_opened[0] || 0;
        },
        /**
         * Genau EIN fachlicher Save.
         *
         * Verbindlich:
         * - Es wird der vollständige Draft in einem einzigen Request gesendet
         *   (label + content + info).
         * - Der globale StepsStore wird erst nach einem erfolgreichen Save
         *   aktualisiert - das übernimmt der Aufrufer.
         * - Bei einem Fehler bleibt der lokale Draft vollständig erhalten und
         *   der Editor geöffnet.
         * - Doppelklicks werden blockiert: ein laufender Save wird nicht ein
         *   zweites Mal ausgelöst.
         *
         * @param {string|null} field  'custom_css' speichert nur das CSS
         *                             (getrennte Expertenaktion)
         * @returns {Promise<Object>}
         */
        save(field = null) {
            // Doppelklickschutz - kein zweiter paralleler Save.
            if (this.saving) {
                return Promise.reject({reason: 'save_in_progress'});
            }

            const payload = {
                ...(field === 'custom_css'
                    ? {label: this.label, custom_css: this.custom_css}
                    : buildSavePayload(this.draft)),
                base_revision: this.revision ?? 0,
            };

            return new Promise((resolve, reject) => {
                this.saving = true;
                this.processing = true;
                this.save_error = null;

                const url = window.route('steps.update', {step: this.uuid});

                api.client.post(url, payload)
                    .then(response => {
                        /*
                         * Die neue Marke ist der Ausgangspunkt für das
                         * nächste Speichern aus demselben offenen Editor.
                         */
                        if (response.data.revision !== undefined) {
                            this.revision = response.data.revision;
                        }

                        // Show "saved" Feedback only if we actually saved!
                        if (response.data.saved) {
                            const campaignStore = useCampaignStore();
                            campaignStore.snackbarText = t('editor.step.saved');
                            campaignStore.showSnackbar = true;
                        }

                        /**
                         * Der gespeicherte Stand ist jetzt der neue
                         * Ausgangszustand - der Editor gilt als nicht mehr
                         * geändert.
                         */
                        this.captureOriginal();

                        resolve(response.data.step);
                    })
                    .catch(error => {
                        /**
                         * Der Draft wird hier ausdrücklich NICHT angetastet.
                         *
                         * Bewusst wird NICHT campaignStore.handleExpiredEditorSession()
                         * aufgerufen: Diese Methode lädt die Seite nach 1,5
                         * Sekunden neu und würde damit den gesamten lokalen
                         * Entwurf vernichten. Genau das darf ein Save-Fehler
                         * nicht tun.
                         *
                         * Stattdessen bleibt der Editor offen, der Entwurf
                         * erhalten und der Benutzer bekommt eine Anweisung, wie
                         * er weiterkommt.
                         *
                         * @see resources/js/Stores/CampaignStore.js -> handleExpiredEditorSession()
                         */
                        if (api.isSessionExpiredError(error)) {
                            this.save_error = t('editor.step.errors.session_expired');

                            reject({...(error.response?.data || {}), reason: 'session_expired'});
                            return;
                        }

                        this.save_error = error.response?.data?.message
                            ?? t('editor.step.errors.save_failed');

                        console.error('Step save failed:', error); // i18n-ignore (Konsole)
                        reject(error.response?.data || error);
                    })
                    .finally(() => {
                        this.saving = false;
                        this.processing = false;
                    });
            });
        },
        getAssetUploadUrl() {
            return window.route('assets.storeForStep', {step: this.uuid});
        },
        getAssetDeleteUrl(json_path) {
            return window.route('assets.deleteCustomised', {
                step: this.uuid,
                json_path: json_path,
            });
        }
    }
});
